import { useCallback, useEffect, useRef, useState } from 'react';
import { env } from '../config/env';
import { getAccessToken } from '../lib/auth/storage';
import {
  handleChunkEvent,
  handleEndEvent,
  handleErrorEvent,
  handleToolCallStartEvent,
  handleToolResultEvent,
} from './useAgentStreamHandlers';

/**
 * Wire shape of an SSE chunk on the agent stream.
 * Issue #1432 Phase 2 — extended with `tool_call_start` and `tool_result`.
 */
interface SSEChunk {
  type: 'metadata' | 'content' | 'done' | 'sources' | 'error' | 'tool_call_start' | 'tool_result';
  content?: string;
  metadata?: Record<string, unknown>;
  sources?: { id: string; content: string; metadata?: Record<string, unknown>; score?: number }[];
  error?: string;
  toolCallId?: string;
  toolName?: string;
  toolArgs?: Record<string, unknown>;
  data?: unknown;
  durationMs?: number;
}

/** Per-message tool execution record. Issue #1432 Phase 2. */
export interface ToolResult {
  toolCallId: string;
  toolName: string;
  data?: unknown;
  error?: string;
  durationMs?: number;
  pending?: boolean;
}

export interface Message {
  /** Server-side message id (assigned post-persist via metadata SSE chunk). Issue #1434. */
  id?: string;
  /** Parent message id (for branched conversations). Issue #1434. */
  parentMessageId?: string | null;
  role: 'user' | 'assistant';
  content: string;
  streaming?: boolean;
  sources?: {
    id: string;
    content: string;
    metadata?: Record<string, unknown>;
    score?: number;
  }[];
  /** Structured tool results for Generative UI rendering. Issue #1432 Phase 2. */
  toolResults?: ToolResult[];
}

/**
 * Walk back from the end of the array and stamp `id` onto the latest message
 * matching `role` that doesn't already have one. Issue #1434.
 */
function stampLatestRoleId(messages: Message[], role: 'user' | 'assistant', id: string): Message[] {
  for (let i = messages.length - 1; i >= 0; i--) {
    const msg = messages[i];
    if (msg && msg.role === role && msg.id == null) {
      const updated: Message = { ...msg, id };
      return [...messages.slice(0, i), updated, ...messages.slice(i + 1)];
    }
  }
  return messages;
}

interface UseAgentStreamOptions {
  /** Portal context for knowledge base scoping (business, consumer, admin, auth) */
  portal?: 'business' | 'consumer' | 'admin' | 'auth';
  /** Initial messages to populate from history */
  initialMessages?: Message[];
  /** Callback fired when a user message is sent (for persistence) */
  onUserMessage?: (content: string) => void;
  /** Callback fired when an assistant message completes streaming (for persistence) */
  onAssistantMessage?: (content: string) => void;
}

interface UseAgentStreamResult {
  messages: Message[];
  isStreaming: boolean;
  error: string | null;
  /**
   * Send a message to the agent. Issue #1434 — `fromMessageId` triggers a
   * fork-from-message: the new user turn is persisted as a sibling under
   * `fromMessageId`, used by assistant-ui's edit-resubmit and regenerate flows.
   */
  sendMessage: (agentId: string, message: string, conversationId?: string, fromMessageId?: string) => Promise<void>;
  clearMessages: () => void;
  /** Replace all messages (used when loading history) */
  setMessages: (messages: Message[]) => void;
}

export const useAgentStream = (options: UseAgentStreamOptions = {}): UseAgentStreamResult => {
  const { portal = 'consumer', initialMessages, onUserMessage, onAssistantMessage } = options;
  const [messages, setMessages] = useState<Message[]>(initialMessages ?? []);
  const [isStreaming, setIsStreaming] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const eventSourceRef = useRef<EventSource | null>(null);
  // Track the assistant content as it streams, for persistence callback
  const assistantContentRef = useRef<string>('');
  // Track connection timeout so it can be cleared from any terminal path
  const connectionTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Cleanup on unmount to avoid timeouts/handlers firing after unmount.
  //
  // Defer the close to a microtask so a fast remount (React concurrent
  // rendering, parent re-render after router.replace from a child useEffect)
  // can cancel it. Without the defer, a transient remount cycle kills the
  // live EventSource right after creation — see business useAgentStream for
  // the full diagnosis.
  const pendingCloseRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  useEffect(() => {
    if (pendingCloseRef.current != null) {
      clearTimeout(pendingCloseRef.current);
      pendingCloseRef.current = null;
    }
    return () => {
      pendingCloseRef.current = setTimeout(() => {
        pendingCloseRef.current = null;
        if (connectionTimeoutRef.current != null) {
          clearTimeout(connectionTimeoutRef.current);
          connectionTimeoutRef.current = null;
        }
        if (eventSourceRef.current != null) {
          eventSourceRef.current.close();
          eventSourceRef.current = null;
        }
      }, 50);
    };
  }, []);

  // Sync initial messages when they change (e.g., history loads after mount)
  const initialMessagesRef = useRef(initialMessages);
  useEffect(() => {
    if (initialMessages && initialMessages !== initialMessagesRef.current) {
      initialMessagesRef.current = initialMessages;
      // Only replace if there are no locally-added messages yet
      setMessages((prev) => {
        if (prev.length === 0 || (initialMessagesRef.current && prev === initialMessagesRef.current)) {
          return initialMessages;
        }
        return prev;
      });
    }
  }, [initialMessages]);

  const sendMessage = useCallback(
    async (agentId: string, messageText: string, conversationId?: string, fromMessageId?: string): Promise<void> => {
      setError(null);
      setIsStreaming(true);
      assistantContentRef.current = '';

      // Add user message — Issue #1434 carries parentMessageId for branch tracking.
      const userMessage: Message = {
        role: 'user',
        content: messageText,
        ...(fromMessageId ? { parentMessageId: fromMessageId } : {}),
      };
      setMessages((prev) => [...prev, userMessage]);

      // Persist user message
      onUserMessage?.(messageText);

      const token = getAccessToken();
      if (token == null) {
        setError('Not authenticated');
        setIsStreaming(false);
        return;
      }

      // Build query params
      const params = new URLSearchParams({
        message: messageText,
        ...(conversationId != null && { conversationId }),
        ...(fromMessageId != null && { fromMessageId }),
        token, // Send token as query param (EventSource doesn't support headers)
        portal, // Portal context for knowledge base scoping
      });

      // Create EventSource for SSE
      const url = `${env.VITE_API_URL}/agents/${agentId}/invoke/stream?${params.toString()}`;

      try {
        const eventSource = new EventSource(url);
        eventSourceRef.current = eventSource;

        let currentContent = '';
        let sources: Message['sources'] = [];

        // Connection timeout — if no message received within 15s, treat as unavailable
        connectionTimeoutRef.current = setTimeout(() => {
          connectionTimeoutRef.current = null;
          if (currentContent === '' && eventSource.readyState !== EventSource.CLOSED) {
            console.error('Agent stream connection timed out');
            setError('AI assistant is temporarily unavailable. Please try again later.');
            eventSource.close();
            setIsStreaming(false);
          }
        }, 15000);

        eventSource.onmessage = (event: Event): void => {
          if (connectionTimeoutRef.current != null) {
            clearTimeout(connectionTimeoutRef.current);
            connectionTimeoutRef.current = null;
          }
          try {
            // event.data is typed as any in EventSource API
            // eslint-disable-next-line @typescript-eslint/no-unsafe-argument
            const chunk = JSON.parse((event as MessageEvent).data) as SSEChunk;

            switch (chunk.type) {
              case 'metadata': {
                // Issue #1434 — server-assigned message ids flow through metadata
                // chunks. Stamp them onto the most recent matching role.
                const userMessageId =
                  chunk.metadata && typeof chunk.metadata.userMessageId === 'string'
                    ? chunk.metadata.userMessageId
                    : undefined;
                if (userMessageId) {
                  setMessages((prev) => stampLatestRoleId(prev, 'user', userMessageId));
                }
                const assistantMessageId =
                  chunk.metadata && typeof chunk.metadata.assistantMessageId === 'string'
                    ? chunk.metadata.assistantMessageId
                    : undefined;
                if (assistantMessageId) {
                  setMessages((prev) => stampLatestRoleId(prev, 'assistant', assistantMessageId));
                }
                break;
              }

              case 'content':
                currentContent = handleChunkEvent(chunk, setMessages, currentContent);
                assistantContentRef.current = currentContent;
                break;

              case 'sources':
                sources = chunk.sources ?? [];
                break;

              case 'tool_call_start':
                if (typeof chunk.toolCallId === 'string' && typeof chunk.toolName === 'string') {
                  handleToolCallStartEvent(chunk.toolCallId, chunk.toolName, setMessages);
                }
                break;

              case 'tool_result':
                if (typeof chunk.toolCallId === 'string' && typeof chunk.toolName === 'string') {
                  handleToolResultEvent(
                    chunk.toolCallId,
                    chunk.toolName,
                    chunk.data,
                    chunk.error,
                    chunk.durationMs,
                    setMessages,
                  );
                }
                break;

              case 'done':
                handleEndEvent(sources, setMessages, setIsStreaming, eventSource);
                // Persist assistant response after stream completes
                if (assistantContentRef.current) {
                  onAssistantMessage?.(assistantContentRef.current);
                }
                break;

              case 'error':
                handleErrorEvent(chunk, setError, setIsStreaming, eventSource);
                break;
            }
          } catch (parseErr) {
            console.error('Failed to parse SSE chunk:', parseErr);
          }
        };

        eventSource.onerror = (): void => {
          if (connectionTimeoutRef.current != null) {
            clearTimeout(connectionTimeoutRef.current);
            connectionTimeoutRef.current = null;
          }
          console.error('EventSource error — agent streaming may not be available at:', url.split('?')[0]);
          setError('AI assistant is temporarily unavailable. Please try again later.');
          eventSource.close();
          setIsStreaming(false);
        };
      } catch (err) {
        const message = err instanceof Error ? err.message : 'Failed to start stream';
        setError(message);
        setIsStreaming(false);
      }
    },
    [portal, onUserMessage, onAssistantMessage],
  );

  const clearMessages = useCallback((): void => {
    if (connectionTimeoutRef.current != null) {
      clearTimeout(connectionTimeoutRef.current);
      connectionTimeoutRef.current = null;
    }
    if (eventSourceRef.current != null) {
      eventSourceRef.current.close();
    }
    setMessages([]);
    setError(null);
    setIsStreaming(false);
  }, []);

  return {
    messages,
    isStreaming,
    error,
    sendMessage,
    clearMessages,
    setMessages,
  };
};
